import { localizeRuntimeText, t } from "./i18n";
import { renderZiarcadiaIcon } from "./ziarcadiaIcons";
import type { VocabularySyncStatus } from "./vocabularyDraftSync";
import { createVocabularyReviewAppearance } from "./vocabularyReviewAppearance";
import { centerReviewWord, observeReviewGeometry, renderReviewList, renderReviewNavigation, renderReviewTable, reviewIndexForKey, syncReviewSelection } from "./vocabularyReviewList";

export type VocabularyPair = { english: string; korean: string };
export type VocabularyReviewToolState = { sourceText: string; index: number };
const DRAFT_KEY = "ziarcadia:vocabulary-review:draft";
const MAX_SOURCE_LENGTH = 80_000;
const TRANSLATION_BATCH_SIZE = 160;
const escapeHtml = (value: string): string => value.replace(/[&<>"']/g, (character) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!);
const inline = (value: string): string => value.replace(/\s+/g, " ").trim();
const hasKorean = (value: string): boolean => /[\u1100-\u11ff\u3130-\u318f\uac00-\ud7af]/u.test(value);

/** One vocabulary item per line; unlike game sources, neighboring sentences are never consumed as clues. */
export const parseVocabularyReviewSource = (source: string): VocabularyPair[] => {
    const pairs: VocabularyPair[] = [];
    for (const raw of source.replace(/\r\n?/g, "\n").split("\n")) {
        const line = raw.trim().replace(/^(?:\d+[.)]\s+|[-*•]\s+)/u, "");
        if (!line) continue;
        const separated = line.match(/^(.*?)(?:\t+|\s*[;=|]\s*|\s+[-–—]\s+|:\s+|,\s*)(.*)$/u);
        const spaced = !separated && line.match(/^(.+?)\s+([\u1100-\u11ff\u3130-\u318f\uac00-\ud7af].*)$/u);
        const fields = separated || spaced;
        if (fields) {
            let english = inline(fields[1] ?? "");
            let korean = inline((fields[2] ?? "").split(/[;\t]/u)[0] ?? "");
            if (hasKorean(english) && /[a-z]/i.test(korean) && !hasKorean(korean)) [english, korean] = [korean, english];
            if (/^(?:english|word|term)$/i.test(english) && /^(?:korean|translation|한국어|번역)$/i.test(korean)) continue;
            if (english) pairs.push({ english, korean });
        } else if (hasKorean(line) && !/[a-z]/i.test(line) && pairs.length && !pairs[pairs.length - 1]!.korean) {
            pairs[pairs.length - 1]!.korean = inline(line);
        } else {
            pairs.push({ english: inline(line), korean: "" });
        }
    }
    return pairs;
};

export const createInitialVocabularyReviewToolState = (): VocabularyReviewToolState => {
    try {
        return { sourceText: (localStorage.getItem(DRAFT_KEY) ?? "").slice(0, MAX_SOURCE_LENGTH), index: 0 };
    } catch {
        return { sourceText: "", index: 0 };
    }
};

export const createVocabularyReviewTool = (root: HTMLElement, state: VocabularyReviewToolState, options: {
    onSourceChange?: (source: string) => void;
    onRetrySync?: () => void;
    syncStatus?: VocabularySyncStatus;
} = {}) => {
    let entries = parseVocabularyReviewSource(state.sourceText);
    let reviewing = false;
    let loading = false;
    let error = "";
    let destroyed = false;
    let request: AbortController | null = null;
    let revision = 0;
    let savedBodyOverflow = "";
    let translationsVisible = true;
    let syncStatus: VocabularySyncStatus = options.syncStatus ?? "local";
    const appearance = createVocabularyReviewAppearance(root, "petrol");
    const stopObservingReview = observeReviewGeometry(root, () => reviewing ? state.index : null);

    const save = () => {
        if (options.onSourceChange) { options.onSourceChange(state.sourceText); return; }
        try { localStorage.setItem(DRAFT_KEY, state.sourceText); } catch { /* A blocked store must not prevent review. */ }
    };
    const iconButton = (action: string, icon: Parameters<typeof renderZiarcadiaIcon>[0], label: string) =>
        `<button type="button" class="secondary-button vocabulary-icon-button" data-vocabulary-action="${action}" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}">${renderZiarcadiaIcon(icon)}</button>`;
    const tableMarkup = () => renderReviewTable({ entries, index: state.index });
    const updateControls = () => {
        state.index = Math.min(Math.max(0, state.index), Math.max(0, entries.length - 1));
        const count = root.querySelector<HTMLElement>("[data-vocabulary-count]");
        if (count) count.textContent = reviewing ? `${state.index + 1} / ${entries.length}` : String(entries.length);
        const translate = root.querySelector<HTMLButtonElement>('[data-vocabulary-action="translate"]');
        if (translate) {
            translate.disabled = loading || !entries.some((entry) => !entry.korean);
            translate.innerHTML = `${renderZiarcadiaIcon("translate")} ${escapeHtml(t(loading ? "vocabularyTranslating" : "vocabularyTranslate"))}`;
        }
        const start = root.querySelector<HTMLButtonElement>('[data-vocabulary-action="review"]');
        if (start) start.disabled = !entries.length || loading;
        const status = root.querySelector<HTMLElement>(".vocabulary-review-status");
        if (status) { status.textContent = error; status.hidden = !error; }
        const sync = root.querySelector<HTMLElement>("[data-vocabulary-sync]");
        if (sync) {
            const labels = { local: "vocabularySavedBrowser", loading: "vocabularyLoadingAccount", saving: "vocabularySavingAccount", saved: "vocabularySavedAccount", error: "vocabularySyncFailed", "identity-error": "vocabularyIdentityFailed", conflict: "vocabularySyncConflict" } as const;
            sync.dataset.status = syncStatus;
            sync.innerHTML = `<span role="status">${escapeHtml(t(labels[syncStatus]))}</span>${syncStatus === "error" || syncStatus === "identity-error" || syncStatus === "conflict" ? `<button type="button" data-vocabulary-action="retry-sync">${escapeHtml(t(syncStatus === "conflict" ? "vocabularySaveMine" : "vocabularyRetrySync"))}</button>` : ""}`;
        }
        syncReviewSelection(root, state.index, entries.length);
    };
    const updatePreview = () => {
        const preview = root.querySelector<HTMLElement>(".vocabulary-review-scroll");
        if (preview) preview.innerHTML = entries.length ? tableMarkup() : `<div class="vocabulary-review-empty">${renderZiarcadiaIcon("wordBank")}<p>${escapeHtml(t("vocabularyEmpty"))}</p></div>`;
        updateControls();
    };
    const render = () => {
        root.innerHTML = reviewing ? `
            <section class="vocabulary-review-tool is-reviewing ${translationsVisible ? "" : "is-meanings-hidden"}" aria-label="${escapeHtml(t("vocabularyReviewTool"))}">
                <header class="vocabulary-review-toolbar">
                    <button type="button" class="secondary-button" data-vocabulary-action="edit">${renderZiarcadiaIcon("edit")} ${escapeHtml(t("vocabularyEdit"))}</button>
                    ${renderReviewNavigation(state.index, entries.length)}
                    <div class="vocabulary-review-utilities">
                        ${appearance.renderButton()}
                        ${iconButton("meanings", translationsVisible ? "eye" : "eyeOff", t(translationsVisible ? "vocabularyHide" : "vocabularyShow"))}
                        ${iconButton("fullscreen", "expand", t("fullscreen"))}
                    </div>
                </header>
                ${renderReviewList({ entries, index: state.index })}
            </section>` : `
            <section class="vocabulary-review-tool">
                <article class="card vocabulary-review-editor">
                    <div class="vocabulary-review-editor-head"><label for="vocabulary-review-source">${escapeHtml(t("vocabularyPaste"))}</label><span data-vocabulary-count class="vocabulary-review-count"></span>${appearance.renderButton()}</div>
                    <textarea id="vocabulary-review-source" spellcheck="false" maxlength="${MAX_SOURCE_LENGTH}" placeholder="${escapeHtml(t("vocabularyPlaceholder"))}">${escapeHtml(state.sourceText)}</textarea>
                    <div class="vocabulary-review-actions"><button type="button" class="secondary-button" data-vocabulary-action="translate"></button><button type="button" class="primary-button" data-vocabulary-action="review">${renderZiarcadiaIcon("play")} ${escapeHtml(t("vocabularyStart"))}</button></div>
                    <p class="vocabulary-review-status" role="alert" hidden></p>
                    <div class="vocabulary-review-sync" data-vocabulary-sync></div>
                </article>
                <article class="vocabulary-review-preview"><div class="vocabulary-review-scroll" tabindex="0" role="region" aria-label="${escapeHtml(t("vocabularyPreview"))}"></div></article>
            </section>`;
        if (reviewing) { updateControls(); centerReviewWord(root, state.index, false); }
        else updatePreview();
        appearance.refresh();
    };
    const select = (index: number) => {
        state.index = Math.min(Math.max(index, 0), Math.max(0, entries.length - 1));
        updateControls();
        if (reviewing) centerReviewWord(root, state.index);
    };
    const stopReview = () => {
        reviewing = false;
        document.body.style.overflow = savedBodyOverflow;
        if (document.fullscreenElement === root) void document.exitFullscreen().catch(() => {});
        render();
        root.querySelector<HTMLButtonElement>('[data-vocabulary-action="review"]')?.focus();
    };
    const translate = async () => {
        if (loading) return;
        const originalRevision = revision;
        const snapshot = entries.map((entry) => ({ ...entry }));
        const pending = snapshot.map((entry, index) => ({ index, term: entry.english, currentTranslation: entry.korean, context: "" })).filter((entry) => !entry.currentTranslation);
        if (!pending.length) return;
        // Match the existing service limits without silently truncating classroom lists or terms.
        if (pending.some((entry) => entry.term.length > 160)) { error = t("vocabularyTermTooLong"); updateControls(); return; }
        const controller = new AbortController();
        request = controller;
        loading = true;
        error = "";
        updateControls();
        const timeout = window.setTimeout(() => controller.abort(), 120_000);
        try {
            for (let offset = 0; offset < pending.length; offset += TRANSLATION_BATCH_SIZE) {
                const batch = pending.slice(offset, offset + TRANSLATION_BATCH_SIZE);
                const response = await fetch("/api/source-translation/generate", {
                    method: "POST", headers: { "Content-Type": "application/json" }, signal: controller.signal,
                    body: JSON.stringify({ mode: "picture-detectives", action: "add", sourceText: batch.map((entry) => entry.term).join("\n"), items: batch })
                });
                const payload = await response.json() as { error?: string; items?: Array<{ index: number; translation: string }> };
                if (!response.ok) throw new Error(payload.error || t("vocabularyTranslationFailed"));
                const expected = new Set(batch.map((entry) => entry.index));
                if (!Array.isArray(payload.items) || payload.items.length !== expected.size) throw new Error(t("vocabularyTranslationFailed"));
                for (const item of payload.items) {
                    if (!item || !Number.isSafeInteger(item.index) || !expected.delete(item.index) || typeof item.translation !== "string" || !inline(item.translation) || item.translation.length > 240) throw new Error(t("vocabularyTranslationFailed"));
                    snapshot[item.index]!.korean = inline(item.translation);
                }
            }
            // Applying a response is atomic; editing, leaving, or a bad batch must never replace newer work.
            if (destroyed || revision !== originalRevision) return;
            entries = snapshot;
            state.sourceText = entries.map((entry) => `${entry.english}; ${entry.korean}`).join("\n");
            revision += 1;
            save();
            const textarea = root.querySelector<HTMLTextAreaElement>("#vocabulary-review-source");
            if (textarea) textarea.value = state.sourceText;
            updatePreview();
        } catch (cause) {
            if (!destroyed && revision === originalRevision) error = controller.signal.aborted ? t("vocabularyTranslationFailed") : localizeRuntimeText(cause instanceof Error ? cause.message : t("vocabularyTranslationFailed"));
        } finally {
            window.clearTimeout(timeout);
            if (request === controller) { request = null; loading = false; if (!destroyed) updateControls(); }
        }
    };
    const onInput = (event: Event) => {
        const target = event.target;
        if (!(target instanceof HTMLTextAreaElement) || target.id !== "vocabulary-review-source") return;
        revision += 1;
        request?.abort();
        state.sourceText = target.value;
        state.index = 0;
        entries = parseVocabularyReviewSource(state.sourceText);
        error = "";
        save();
        updatePreview();
    };
    const onClick = (event: Event) => {
        const target = event.target as HTMLElement | null;
        const reviewButton = target?.closest<HTMLElement>("[data-review-index]");
        if (reviewButton) { select(Number(reviewButton.dataset.reviewIndex)); return; }
        const row = target?.closest<HTMLElement>("[data-vocabulary-row]");
        if (row) { select(Number(row.dataset.vocabularyRow)); return; }
        const action = target?.closest<HTMLElement>("[data-vocabulary-action]")?.dataset.vocabularyAction;
        if (action === "translate") void translate();
        if (action === "retry-sync") options.onRetrySync?.();
        if (action === "review" && entries.length && !loading) {
            savedBodyOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            reviewing = true;
            render();
            root.querySelector<HTMLElement>(".vocabulary-review-scroll")?.focus({ preventScroll: true });
        }
        if (action === "edit") stopReview();
        if (action === "meanings") {
            translationsVisible = !translationsVisible;
            root.querySelector(".vocabulary-review-tool")?.classList.toggle("is-meanings-hidden", !translationsVisible);
            const button = target?.closest<HTMLButtonElement>("button");
            if (button) {
                button.innerHTML = renderZiarcadiaIcon(translationsVisible ? "eye" : "eyeOff");
                button.setAttribute("aria-label", t(translationsVisible ? "vocabularyHide" : "vocabularyShow"));
                button.title = button.getAttribute("aria-label")!;
            }
        }
        if (action === "fullscreen") {
            if (document.fullscreenElement === root) void document.exitFullscreen().catch(() => {});
            else if (root.requestFullscreen) void root.requestFullscreen().catch(() => { /* Review still fills the viewport on unsupported devices. */ });
        }
    };
    const onKeyDown = (event: KeyboardEvent) => {
        if (!reviewing || event.altKey || event.ctrlKey || event.metaKey || event.isComposing) return;
        if (event.key === "Escape") { event.preventDefault(); stopReview(); return; }
        const next = reviewIndexForKey(event, state.index, entries.length);
        if (next !== null) {
            event.preventDefault();
            select(next);
        }
    };
    const onFullscreen = () => {
        const button = root.querySelector<HTMLButtonElement>('[data-vocabulary-action="fullscreen"]');
        if (button) { button.title = t(document.fullscreenElement === root ? "exitFullscreen" : "fullscreen"); button.setAttribute("aria-label", button.title); }
    };
    root.addEventListener("input", onInput);
    root.addEventListener("click", onClick);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("fullscreenchange", onFullscreen);
    render();
    return {
        setSyncStatus: (status: VocabularySyncStatus) => { syncStatus = status; updateControls(); },
        restoreSource: (source: string) => {
            revision += 1;
            request?.abort();
            state.sourceText = source;
            state.index = 0;
            entries = parseVocabularyReviewSource(source);
            error = "";
            if (reviewing) stopReview();
            else render();
        },
        refreshLanguage: () => { render(); onFullscreen(); },
        destroy: () => {
            appearance.destroy();
            stopObservingReview();
            destroyed = true;
            request?.abort();
            if (reviewing) document.body.style.overflow = savedBodyOverflow;
            if (document.fullscreenElement === root) void document.exitFullscreen().catch(() => {});
            root.removeEventListener("input", onInput);
            root.removeEventListener("click", onClick);
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("fullscreenchange", onFullscreen);
        }
    };
};
